import { useCallback, useRef, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { OrderDetailContent } from './OrderDetailContent'
import { shouldCloseMobileSheetDrag } from '../mobileSheetDrag'
import type { DeepOrder } from '../types'

interface MobileOrderDetailSheetProps {
  order: DeepOrder | undefined
  isOpen: boolean
  onClose: () => void
  onStatusChange: (orderId: number, status: string) => void
  onCancelOrder: () => void
  orderConfirmationEnabled?: boolean
  onConfirmOrder?: () => void
  onDeclineOrder?: () => void
  isUpdating?: boolean
  isConfirming?: boolean
  isDeclining?: boolean
}

export function MobileOrderDetailSheet({
  order,
  isOpen,
  onClose,
  onStatusChange,
  onCancelOrder,
  orderConfirmationEnabled = false,
  onConfirmOrder,
  onDeclineOrder,
  isUpdating = false,
  isConfirming = false,
  isDeclining = false,
}: MobileOrderDetailSheetProps) {
  const { t } = useTranslation(['orders', 'common'])
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startY = useRef(0)

  const resetDrag = useCallback(() => {
    setDragOffset(0)
    setIsDragging(false)
  }, [])

  const handleClose = useCallback(() => {
    resetDrag()
    onClose()
  }, [onClose, resetDrag])

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleClose()
      return
    }
    resetDrag()
  }, [handleClose, resetDrag])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return

    startY.current = event.clientY
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return

    const diff = event.clientY - startY.current
    setDragOffset(Math.max(0, diff))
  }, [isDragging])

  const handlePointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const shouldClose = shouldCloseMobileSheetDrag(dragOffset)
    resetDrag()

    if (shouldClose) {
      onClose()
    }
  }, [dragOffset, isDragging, onClose, resetDrag])

  if (!order) return null

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[90vh] tablet:h-[85vh] flex flex-col p-0 rounded-t-xl overflow-hidden [&>button]:hidden"
        style={{
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out',
        }}
      >
        <SheetTitle className="sr-only">
          {t('orders:order.orderId', { id: order.id })}
        </SheetTitle>

        <div
          className="relative flex h-12 flex-shrink-0 items-center justify-center border-b border-border/60 bg-background/95"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="h-1.5 w-12 rounded-full bg-muted-foreground/35" />
          <button
            type="button"
            aria-label={t('common:actions.close')}
            onClick={handleClose}
            onPointerDown={(event) => event.stopPropagation()}
            className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 tablet:p-6">
            <OrderDetailContent
              order={order}
              orderConfirmationEnabled={orderConfirmationEnabled}
              onStatusChange={onStatusChange}
              onCancelOrder={onCancelOrder}
              onConfirmOrder={onConfirmOrder}
              onDeclineOrder={onDeclineOrder}
              onAfterTimeAction={handleClose}
              isUpdating={isUpdating}
              isConfirming={isConfirming}
              isDeclining={isDeclining}
            />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
